import { useState, useCallback } from 'react';
import { IEnhancedJobData, IJobEnhancePayload, enhanceJobDetails } from 'wisestep-api';

interface UseJobEnhancementReturn {
  enhancedData: IEnhancedJobData | null;
  isLoading: boolean;
  isAIEnhanced: boolean;
  enhanceJob: (payload: IJobEnhancePayload) => Promise<IEnhancedJobData | null>;
  resetEnhancement: () => void;
}

export const useJobEnhancement = (): UseJobEnhancementReturn => {
  const [enhancedData, setEnhancedData] = useState<IEnhancedJobData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isAIEnhanced, setIsAIEnhanced] = useState(false);

  const enhanceJob = useCallback(
    async (payload: IJobEnhancePayload): Promise<IEnhancedJobData | null> => {
      try {
        setIsLoading(true);
        const response = await enhanceJobDetails(payload);
        if (response && response?.data && response?.data?.jobDescription?.trim().length > 0) {
          setEnhancedData(response?.data);
          setIsAIEnhanced(true);
          setIsLoading(false);
          return response?.data;
        } else {
          setEnhancedData(null);
          setIsAIEnhanced(false);
        }
        return null;
      } catch (error) {
        console.error('Error during job enhancement:', error);
        setEnhancedData(null);
        setIsAIEnhanced(false);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const resetEnhancement = useCallback(() => {
    setEnhancedData(null);
    setIsAIEnhanced(false);
    setIsLoading(false);
  }, []);

  return {
    enhancedData,
    isLoading,
    isAIEnhanced,
    enhanceJob,
    resetEnhancement,
  };
};
